import React, { useRef, useState, useEffect } from 'react';
import { LinearProgress, Stack } from '@mui/material';
import styled from '@emotion/styled';
import PauseIcon from './Pause';
import PlayIcon from './Play';

export interface EliceAudioPlayerProps {
  src: string;
  mimeType?: string;
  onFileLoaded?: (file: File) => void;
}

const PlayerButton = styled.button`
  display: flex;
  background-color: transparent !important;
  border: none;
`;

const getAccurateDuration = async (src: string): Promise<number | null> => {
  try {
    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    const audioCtx = new AudioContext();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    return audioBuffer.duration;
  } catch (err) {
    console.error('🎧 decodeAudioData 실패:', err);
    return null;
  }
};

const EliceAudioPlayer = ({
  src,
  mimeType = 'audio/mp3',
  onFileLoaded,
}: EliceAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const fetchAndDecode = async () => {
      try {
        const response = await fetch(src);
        const blob = await response.blob();
        const filename = src.split('/').pop() || 'audio.mp3';
        const file = new File([blob], filename, { type: blob.type || mimeType });

        onFileLoaded?.(file);

        const duration = await getAccurateDuration(src);
        if (duration && isFinite(duration)) {
          setDuration(duration)
          setIsReady(true);
        }
      } catch (err) {
        console.error('❌ Fetch or decode 실패:', err);
      }
    };

    if (src) fetchAndDecode();
  }, [src, mimeType, onFileLoaded]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    isPlaying ? audio.pause() : audio.play();
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (duration > 0) {
        const percent = (audio.currentTime / duration) * 100;
        setProgress(percent);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setProgress(100);
      setTimeout(() => {
        setProgress(0);
        setIsPlaying(false);
      }, 300);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [duration]);

  return (
    <Stack spacing={1} alignItems="center" direction="row">
      <audio ref={audioRef} src={src} preload="metadata" />
      <PlayerButton onClick={togglePlay}>
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </PlayerButton>
      <LinearProgress
        variant={isReady ? 'determinate' : 'indeterminate'}
        value={progress}
        sx={{ width: '200px' }}
      />
    </Stack>
  );
};

export default EliceAudioPlayer;
